'use client';

import React from 'react';
import { toast } from 'react-toastify';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import Button from '@/components/V2/Button';
import AuthToast from '@/components/V2/Popup/AuthToast';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useAppDispatch } from '@/store';
import { customerApi } from '@/store/api/customer';
import { useMarkDeviceAsTrustedMutation } from '@/store/api/twoFactorAuthentication';

interface TrustedDeviceProps {
  onYes?: () => void;
  onNo?: () => void;
}

const TrustedDevice: React.FC<TrustedDeviceProps> = ({ onYes = () => {}, onNo = () => {} }) => {
  const { customer } = useAuthContext();
  const [markDeviceAsTrusted, { isLoading: isMarkingTrusted }] = useMarkDeviceAsTrustedMutation();
  const [isNoLoading, setIsNoLoading] = React.useState(false);
  const dispatch = useAppDispatch();

  const handleYes = async () => {
    if (!customer?.customerID) {
      toast(
        ({ closeToast }) => (
          <AuthToast type="error" message="Customer information not available" closeToast={closeToast} />
        ),
        { containerId: AUTH_MODAL_TOAST_CONTAINER }
      );
      return;
    }

    try {
      // Backend handles device trust via cookies - no payload needed
      const response = await markDeviceAsTrusted().unwrap();

      if (response.success) {
        toast(
          ({ closeToast }) => <AuthToast type="success" message="Device marked as trusted" closeToast={closeToast} />,
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
        try {
          dispatch(customerApi.util.invalidateTags(['Customers']));
          await dispatch(customerApi.endpoints.getCustomer.initiate(undefined, { forceRefetch: true }));
        } catch (e) {}
        onYes();
      } else {
        toast(
          ({ closeToast }) => (
            <AuthToast
              type="error"
              message={response.message || 'Failed to mark device as trusted'}
              closeToast={closeToast}
            />
          ),
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
        try {
          dispatch(customerApi.util.invalidateTags(['Customers']));
          await dispatch(customerApi.endpoints.getCustomer.initiate(undefined, { forceRefetch: true }));
        } catch (e) {}
        onYes();
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.data?.exceptionMessage || 'Failed to mark device as trusted';
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
      onYes();
    }
  };

  const handleNo = async () => {
    setIsNoLoading(true);
    // Add 2-second delay to match the timing of Yes click (which has API delay)
    setTimeout(() => {
      setIsNoLoading(false);
      onNo();
    }, 2000);
  };

  return (
    <div className="">
      <h2 className="v2-text-lg md:v2-text-4xl v2-font-sweet-sans-pro-bold v2-font-bold v2-text-[#101828] v2-mb-2">
        Trusted Device?
      </h2>
      <p className="v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-text-[#344054] v2-mb-6 md:v2-mb-8">
        Would you like to add this device to your trusted devices?
      </p>
      <div className="v2-flex v2-items-center v2-gap-4">
        <Button
          btnSize="md"
          scheme="primary"
          variant="filled"
          type="button"
          className="v2-w-full v2-font-sweet-sans-pro-medium v2-font-medium"
          onClick={handleYes}
          disabled={isMarkingTrusted || isNoLoading}
        >
          {isMarkingTrusted ? 'Loading...' : 'Yes'}
        </Button>
        <Button
          btnSize="md"
          scheme="primary"
          variant="outlined"
          type="button"
          className="v2-w-full v2-font-sweet-sans-pro-medium v2-font-medium"
          onClick={handleNo}
          disabled={isMarkingTrusted || isNoLoading}
        >
          {isNoLoading ? 'Loading...' : 'No'}
        </Button>
      </div>
    </div>
  );
};

export default TrustedDevice;
